import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '..';
import { AddressTypes, formatInitialAddress } from '../slices/property.slice';

/**
 * Concatenates address components into a single string.
 * @param {AddressTypes} address - The address object containing various components.
 * @returns {string} The concatenated address string.
 */
const concatenateAddress = (address: AddressTypes) => {
  const {
    buildingNumber,
    mainStreet,
    subStreet,
    barangay,
    city,
    province,
    zipCode,
    country,
  } = address;
  return `${buildingNumber ? `${buildingNumber}, ` : ''}${mainStreet}${
    subStreet ? `, ${subStreet}` : ''
  }, ${barangay}, ${city}, ${province}, ${zipCode}, ${country}`;
};

/**
 * Selects the properties state from the root state.
 * @param {RootState} state - The root state of the Redux store.
 * @returns The properties state.
 */
const selectPropertiesState = (state: RootState) => state.properties;

/**
 * Selects the currently selected property.
 */
export const selectSelectedProperty = createSelector(
  [selectPropertiesState],
  (propertiesState) =>
    propertiesState.properties.find(
      (property) =>
        property.propertyID === propertiesState.selectedProperty.value,
    ),
);

export const selectPropertyID = createSelector(
  [selectSelectedProperty, selectPropertiesState],
  (_, propertiesState) => propertiesState.selectedProperty.value ?? '',
);

export const selectedPropertyID = (state: RootState) =>
  state.properties?.selectedProperty?.value;

/**
 * Selects the current module from the properties state.
 * @returns a string containing the current module name.
 */
export const selectedModule = createSelector(
  [
    selectSelectedProperty,
    (_: RootState, currentModule: string) => currentModule,
  ],
  (selectedProperty, currentModule) => {
    const moduleName = selectedProperty?.userData?.Modules.find(
      (module) => module.Module.Code === currentModule,
    );
    return moduleName?.Module?.Description;
  },
);

/**
 * Selects the details of the currently selected property.
 * @returns An object containing property details.
 */
export const selectPropertyDetails = createSelector(
  [selectSelectedProperty, selectPropertiesState],
  (selectedProperty, propertiesState) => {
    if (!selectedProperty) {
      return {
        propertyId: propertiesState.selectedProperty.value,
        propertyName: propertiesState.selectedProperty.label,
        propertyAddress: '',
        propertyTel: '',
        propertyEmail: '',
        userName: undefined,
      };
    }

    const getPrimaryOrFallback = <T>(
      items: T[] | undefined,
      isPrimary: (item: T) => boolean,
      getValue: (item: T) => string | undefined,
    ): string => {
      if (!items?.length) return '';
      const primary = items.find(isPrimary);
      if (primary) {
        const val = getValue(primary);
        if (val) return val;
      }
      const fallback = items.find((i) => !isPrimary(i));
      return fallback ? (getValue(fallback) ?? '') : '';
    };

    let propertyTel = '';
    if (selectedProperty.contacts) {
      if ('contacts' in selectedProperty.contacts) {
        propertyTel = getPrimaryOrFallback(
          selectedProperty.contacts.contacts,
          (item) => item.contact.isPrimary,
          (item) => item.contact.number?.toString(),
        );
      } else if ('phones' in selectedProperty.contacts) {
        propertyTel = getPrimaryOrFallback(
          selectedProperty.contacts.phones,
          (item) => item.phone.isPrimary,
          (item) => item.phone.number?.toString(),
        );
      }
    }

    const propertyEmail = getPrimaryOrFallback(
      selectedProperty.emails?.emails,
      (item) => item.email.isPrimary,
      (item) => item.email.address?.toString(),
    );

    return {
      propertyId: propertiesState.selectedProperty.value,
      propertyName: propertiesState.selectedProperty.label,
      propertyAddress: formatInitialAddress(selectedProperty.address ?? {}),
      propertyTel,
      propertyEmail,
      userName: selectedProperty.userData?.shortName,

      // new
      propertyRawAddress: selectedProperty.address ?? {},
      currency: '',
      taxInfo: selectedProperty?.taxInfo,
      additionalData: selectedProperty?.additionalData ?? {},
    };
  },
);

/**
 * Selects the list of departments (modules) for the selected property.
 * @returns A sorted array of department names.
 */
export const selectDepartmentList = createSelector(
  [selectSelectedProperty],
  (selectedProperty) => {
    const modules = selectedProperty?.userData?.Modules.map(
      (module) => module.Module.Description,
    );
    return modules ? modules.sort((a, b) => a.localeCompare(b)) : [];
  },
);

/**
 * Selects the list of department (module) codes for the selected property.
 * @returns An array of module objects.
 */
export const selectDepartmentListCode = createSelector(
  [selectSelectedProperty],
  (selectedProperty) =>
    selectedProperty?.userData?.Modules?.map((module) => module.Module) || [],
);

/**
 * Selects the description of the current module.
 * @returns The description of the current module or undefined if not found.
 */
export const selectCurrentModuleDesc = createSelector(
  [
    selectDepartmentListCode,
    (_: RootState, currentModule: string) => currentModule,
  ],
  (departmentListCode, currentModule) =>
    departmentListCode.find((module) => module.Code === currentModule)
      ?.Description,
);

/**
 * Selects the user details associated with the selected property.
 * @returns The user data of the selected property or undefined if not available.
 */
export const selectPropertyUserDetails = createSelector(
  [selectSelectedProperty, selectPropertiesState],
  (selectedProperty) => selectedProperty?.userData,
);

/**
 * Selects the property details based on the provided propertyID.
 * @param {string} propertyID - The ID of the property to select.
 * @returns An object containing property details.
 */
export const selectPropertyDetailsByID = (propertyID: string) =>
  createSelector([selectPropertiesState], (propertiesState) => {
    const selectedProperty = propertiesState.properties.find(
      (property) => property.propertyID === propertyID,
    );
    return {
      propertyId: selectedProperty?.propertyID,
      propertyName: selectedProperty?.propertyName,
      propertyAddress: selectedProperty
        ? concatenateAddress(selectedProperty.address!)
        : '',
      propertyTel: selectedProperty?.contacts?.[0]?.contact?.number || '',
      propertyEmail: selectedProperty?.emails?.[0]?.email || '',
      userName: selectedProperty?.userData?.shortName,
    };
  });

export const selectPropertyImages = createSelector(
  (state: RootState) => state.properties, // Access the properties state
  (propertiesState) => {
    const selectedProperty = propertiesState.selectedProperty;
    const property = propertiesState.properties.find(
      (prop) => prop.propertyID === selectedProperty.value,
    );

    return property?.images || { logo: null, banner: null, reportLogo: null };
  },
);

/**
 * Selects the tax information of the currently selected property as a full object.
 * @returns The full TaxInfoTypes object or undefined.
 */
export const selectPropertyTaxInfo = createSelector(
  [selectSelectedProperty],
  (selectedProperty) => ({
    owner: selectedProperty?.taxInfo?.owner,
    property: selectedProperty?.taxInfo?.property,
    vatType: selectedProperty?.taxInfo?.vatType,
    scRatePercentage: selectedProperty?.taxInfo?.scRatePercentage,
    localTaxPercentage: selectedProperty?.taxInfo?.localTaxPercentage,
  }),
);

export const selectActiveProperties = (state: RootState) =>
  state.properties.activeProperties;

export const selectHasBO = createSelector(
  [selectActiveProperties],
  (activeProperties) =>
    activeProperties.some(
      (item) => item.link === '/back-office' && item.isActive,
    ),
);

export const selectHasOnlyPOS = createSelector(
  [selectActiveProperties],
  (activeProperties) => {
    const activeItems = activeProperties.filter((item) => item.isActive);

    return (
      activeItems.length > 0 &&
      activeItems.every((item) => item.link === '/point-of-sale')
    );
  },
);

export const selectProperty = createSelector(
  [selectPropertiesState],
  (properties) => properties.selectedProperty,
);
